import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { addDoc, collection, Firestore } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { FIREBASE_FIRESTORE, FIREBASE_FUNCTIONS } from '@pedix-workspace/angular-firebase';
import { CardModel, createZenriseCardToken, stripDocumentMask } from '@pedix-workspace/angular-ui-payment';
import { addDays, format } from 'date-fns';
import { AnalyticsService } from '../shared/analytics.service';
import { PlansService } from '@pedix-workspace/data-access';
import {
  FunctionResponseClient,
  RegistrationData,
  StripeSubscriptionParams,
  StripeSubscriptionResponse,
  ZenriseBankAccount,
  ZenriseDebtData,
  ZenriseNewSubscriptionReactivation,
  ZenrisePaymentMethodType,
  ZenriseBank,
  ZenriseReactivateSubscription,
  ZenriseSubscriptions,
} from '@pedix-workspace/utils';
import { Observable, firstValueFrom, shareReplay } from 'rxjs';
import { RegistrationSharedService } from './registration-shared.service';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../environments/environment';

const ZENRISE_SDK_ENVIRONMENT = environment.production ? 'PROD' : 'DEV';


type ErrorMapping = { keywords: string[]; message: string };

const ZENRISE_GENERIC_CARD_ERROR = 'Hubo un problema procesando el pago con esta tarjeta. Por favor, intenta con una tarjeta diferente.';
const ZENRISE_AUTH_REQUIRED_ERROR = 'Tu tarjeta requiere autorización. Contacta a tu banco al número que figura en el dorso de la tarjeta para autorizar el pago.';
const STRIPE_CURRENCY_CONFLICT_ERROR = 'Ya tienes una suscripción activa con una moneda diferente. No es posible combinar monedas en la misma cuenta. Por favor, contacta a soporte para resolver este inconveniente.';
const STRIPE_DECLINED_ERROR = 'Tu tarjeta fue rechazada por tu banco. Por favor, intenta con otra tarjeta o contacta a tu banco.';

const ZENRISE_ERROR_MAP: ErrorMapping[] = [
  { keywords: ['no posee fondos suficientes'], message: 'Para validar tu tarjeta, el banco necesita realizar un pequeño cobro temporal. Asegúrate de tener fondos disponibles, el reembolso se realizará al instante.' },
  { keywords: ['problema con bin'], message: 'Hay un problema con el tipo de tarjeta seleccionado. Verifica si es tarjeta de débito o crédito e intenta nuevamente.' },
  { keywords: ['tarjeta expirada'], message: 'Tu tarjeta ha expirado. Por favor, utiliza una tarjeta válida.' },
  { keywords: ['falta autorización'], message: ZENRISE_AUTH_REQUIRED_ERROR },
  { keywords: ['llamar al numero al dorso'], message: ZENRISE_AUTH_REQUIRED_ERROR },
  { keywords: ['interval not found'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['excede ing.de pin'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['tarj. no habilitada'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['no message available'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['tarjeta invalida'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['reintente con otro medio de pago'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['no existe cuenta asociada'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['metodo de pago no permitido'], message: ZENRISE_GENERIC_CARD_ERROR },
  { keywords: ['error en procesar pago'], message: 'Error al procesar el pago. Por favor, intenta nuevamente en unos minutos.' },
  { keywords: ['no está habilitado para débito directo'], message: 'El banco seleccionado no está habilitado para débito directo. Por favor, selecciona otro banco o utiliza tarjeta.' },
  { keywords: ['tokenized card', 'not found'], message: 'Hubo un problema validando la tarjeta. Por favor, intenta nuevamente.' },
];

const STRIPE_ERROR_MAP: ErrorMapping[] = [
  { keywords: ['insufficient funds'], message: 'Tu tarjeta no tiene fondos suficientes. Por favor, utiliza otra tarjeta o recarga saldo.' },
  { keywords: ['security code', 'incorrect'], message: 'El código de seguridad de tu tarjeta es incorrecto. Por favor, verifica el CVV/CVC e intenta nuevamente.' },
  { keywords: ['card number', 'incorrect'], message: 'El número de tarjeta ingresado es incorrecto. Por favor, verifica los datos e intenta nuevamente.' },
  { keywords: ['has expired'], message: 'Tu tarjeta ha expirado. Por favor, utiliza una tarjeta válida.' },
  { keywords: ['expiration month', 'invalid'], message: 'El mes de vencimiento de tu tarjeta es inválido. Por favor, verifica los datos.' },
  { keywords: ['expiration year', 'invalid'], message: 'El año de vencimiento de tu tarjeta es inválido. Por favor, verifica los datos.' },
  { keywords: ['address', 'incorrect'], message: 'La dirección de tu tarjeta es incorrecta. Por favor, verifica los datos de facturación.' },
  { keywords: ['postal code', 'incorrect'], message: 'El código postal de tu tarjeta es incorrecto. Por favor, verifica los datos de facturación.' },
  { keywords: ['card was declined'], message: STRIPE_DECLINED_ERROR },
  { keywords: ['card has been declined'], message: STRIPE_DECLINED_ERROR },
  { keywords: ['error occurred while processing'], message: 'Error al procesar el pago. Por favor, intenta nuevamente en unos minutos.' },
  { keywords: ['payment requires authentication'], message: 'Tu banco requiere autenticación adicional. Por favor, completa la verificación con tu banco.' },
  { keywords: ['cannot combine currencies'], message: STRIPE_CURRENCY_CONFLICT_ERROR },
  { keywords: ['active subscription', 'currency'], message: STRIPE_CURRENCY_CONFLICT_ERROR },
  { keywords: ['customer already has', 'different currency'], message: STRIPE_CURRENCY_CONFLICT_ERROR },
  { keywords: ['use an alternative payment method'], message: 'No pudimos procesar el pago con este método. Por favor, utiliza una tarjeta diferente.' },
  { keywords: ['maximum number of subscriptions'], message: 'Has alcanzado el número máximo de suscripciones permitidas. Contacta a soporte para ayuda.' },
];

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  priceOption: string;
  private banks$: Observable<ZenriseBank[]> | null = null;

  private registrationSharedService = inject(RegistrationSharedService);
  private firestore = inject(FIREBASE_FIRESTORE);
  private functions = inject(FIREBASE_FUNCTIONS);
  private http = inject(HttpClient);
  private analyticsService = inject(AnalyticsService);
  private plansService = inject(PlansService);
  private t = inject(TranslocoService);

  readonly banks = signal<ZenriseBank[]>([]);

  getBanks(): Observable<ZenriseBank[]> {
    if (!this.banks$) {
      this.banks$ = this.http
        .get<ZenriseBank[]>(`${environment.apiUrl}/zenrise/banks`)
        .pipe(shareReplay(1));
    }
    return this.banks$;
  }

  loadBanks() {
    if (this.banks().length > 0) {
      return;
    }
    firstValueFrom(this.getBanks()).then(
      banks => this.banks.set(banks),
      () => this.banks.set([]),
    );
  }

  async createRegistrationData() {
    const { registrationData } = this.registrationSharedService;

    registrationData.created = new Date();

    const registrationDataDocument = await addDoc(collection(this.firestore, 'registrationdata'), {
      ...registrationData,
    });

    this.registrationSharedService.registrationData.id = registrationDataDocument.id;
  }

  private createZenriseSubscription(
    registrationData: RegistrationData,
    paymentToken,
    cardModel: CardModel,
  ) {
    const startDate = format(addDays(new Date(), environment.zenrise.trialDays), 'yyyy-MM-dd');
    const zenriseSubscription: ZenriseSubscriptions = {
      subscriptions: [
        {
          contact: {
            email: registrationData.customer.email,
            firstName: registrationData.customer.firstName,
            lastName: registrationData.customer.lastName,
            holderDocumentNumber: registrationData.customer.dni,
            documentType: registrationData.customer.documentType,
            phoneAreaCode: null,
            phoneNumber: registrationData.customer.phone,
            externalReference: registrationData.id,
          },
          paymentToken,
          externalReference: registrationData.id,
          planId: this.priceOption,
          collectType: 'charge',
          creditCard: cardModel.creditCard,
          startDate,
          metadata: {
            registrationData,
          },
        },
      ],
    };

    const url = `${environment.apiUrl}/zenrise/subscription`;
    return this.http.post(url, zenriseSubscription).toPromise();
  }

  async createArgentineSubscription(cardModel: CardModel) {
    try {
      await this.createRegistrationData();
    } catch (error) {
      throw this.buildMessageError(undefined, 'zenrise');
    }

    this.registrationSharedService.registrationData.customer.dni = stripDocumentMask(cardModel.dni);
    this.registrationSharedService.registrationData.customer.documentType = cardModel.documentType;

    let paymentToken;

    try {
      paymentToken = await createZenriseCardToken(cardModel, ZENRISE_SDK_ENVIRONMENT);
    } catch (error) {
      throw this.buildMessageError(undefined, 'zenrise');
    }

    try {
      await this.createZenriseSubscription(
        this.registrationSharedService.registrationData,
        paymentToken,
        cardModel,
      );
    } catch (error) {
      throw this.buildMessageError(this.extractHttpErrorMessage(error), 'zenrise');
    }
  }

  async createArgentineCbuSubscription(bankAccount: ZenriseBankAccount) {
    try {
      await this.createRegistrationData();
    } catch (error) {
      throw this.buildMessageError(undefined, 'zenrise');
    }

    try {
      await this.createZenriseCbuSubscription(
        this.registrationSharedService.registrationData,
        bankAccount,
      );
    } catch (error) {
      throw this.buildMessageError(this.extractHttpErrorMessage(error), 'zenrise');
    }
  }

  private createZenriseCbuSubscription(
    registrationData: RegistrationData,
    bankAccount: ZenriseBankAccount,
  ) {
    const startDate = format(addDays(new Date(), environment.zenrise.trialDays), 'yyyy-MM-dd');
    const zenriseSubscription: ZenriseSubscriptions = {
      subscriptions: [
        {
          contact: {
            email: registrationData.customer.email,
            firstName: registrationData.customer.firstName,
            lastName: registrationData.customer.lastName,
            holderDocumentNumber: stripDocumentMask(bankAccount.holderIdNumber),
            phoneAreaCode: null,
            phoneNumber: registrationData.customer.phone,
            externalReference: registrationData.id,
          },
          bankAccountDto: bankAccount,
          externalReference: registrationData.id,
          planId: this.priceOption,
          collectType: 'charge',
          startDate,
          metadata: {
            registrationData,
          },
        },
      ],
    };

    const url = `${environment.apiUrl}/zenrise/subscription`;
    return this.http.post(url, zenriseSubscription).toPromise();
  }

  async createArgentineReactivation(cardModel: CardModel, subscriptionDebt: ZenriseDebtData) {
    let paymentToken;

    try {
      paymentToken = await createZenriseCardToken(cardModel, ZENRISE_SDK_ENVIRONMENT);
    } catch (error) {
      throw this.buildMessageError(undefined, 'zenrise');
    }

    const reactivateSubscription: ZenriseReactivateSubscription = {
      extraDays: subscriptionDebt.daysRemaining,
      paymentToken,
      collectPendingInvoice: Boolean(subscriptionDebt.debt),
      creditCard: cardModel.creditCard === 1,
      subscriptionId: +subscriptionDebt.subscription.id,
    };

    try {
      await this.createZenriseReactivation(reactivateSubscription);
    } catch (error) {
      throw this.buildMessageError(this.extractHttpErrorMessage(error), 'zenrise');
    }
  }

  async createNewSubscriptionReactivation(
    cardModel: CardModel,
    paymentMethodType: ZenrisePaymentMethodType,
    subscriptionDebt: ZenriseDebtData,
    bankAccount: ZenriseBankAccount | undefined,
    contact: ZenriseNewSubscriptionReactivation['contact'],
  ) {
    const needsCard = paymentMethodType === 'card' || subscriptionDebt.debt > 0;
    const card = needsCard
      ? {
          cardNumber: cardModel.cardNumber.replace(/ /g, ''),
          expirationMonth: `${cardModel.expirationMonth}`,
          expirationYear: cardModel.expirationYear.substring(2, 4),
          cardHolderName: cardModel.cardName,
          securityCode: cardModel.cvv,
          cardHolderDni: stripDocumentMask(cardModel.dni),
          creditCard: cardModel.creditCard === 1,
          email: contact.email,
        }
      : undefined;

    let paymentToken: ZenriseNewSubscriptionReactivation['paymentToken'];

    if (paymentMethodType === 'card') {
      try {
        paymentToken = await createZenriseCardToken(cardModel, ZENRISE_SDK_ENVIRONMENT);
      } catch (error) {
        throw this.buildMessageError(undefined, 'zenrise');
      }
    }

    const cleanedBankAccount = bankAccount
      ? { ...bankAccount, holderIdNumber: stripDocumentMask(bankAccount.holderIdNumber) }
      : undefined;

    const reactivationData: ZenriseNewSubscriptionReactivation = {
      subscriptionId: +subscriptionDebt.subscription.id,
      extraDays: subscriptionDebt.daysRemaining,
      paymentMethodType,
      paymentToken,
      card,
      bankAccount: cleanedBankAccount,
      contact,
    };

    try {
      await this.createNewSubscriptionReactivationRequest(reactivationData);
    } catch (error) {
      throw this.buildMessageError(this.extractHttpErrorMessage(error), 'zenrise');
    }
  }

  private createNewSubscriptionReactivationRequest(reactivationData: ZenriseNewSubscriptionReactivation) {
    const url = `${environment.apiUrl}/payments/reactivate-new`;
    return this.http.post(url, reactivationData).toPromise();
  }

  private createZenriseReactivation(reactivateSubscription: ZenriseReactivateSubscription) {
    const url = `${environment.apiUrl}/payments/reactivate`;
    return this.http.post(url, reactivateSubscription).toPromise();
  }

  async createInternationalSubscription(stripePayment = null) {
    try {
      await this.createRegistrationData();
    } catch {
      throw this.buildMessageError(undefined, 'stripe');
    }

    try {
      const paymentMethod = stripePayment ? await stripePayment.createPaymentMethod() : null;

      const processSubscriptionFn = httpsCallable<
        StripeSubscriptionParams,
        FunctionResponseClient<StripeSubscriptionResponse>
      >(this.functions, 'stripeProcessSubscription');

      await processSubscriptionFn({
        registrationData: { id: this.registrationSharedService.registrationData.id },
        paymentMethodId: paymentMethod ? paymentMethod.id : undefined,
        priceId: this.priceOption,
        planType: this.plansService.planType,
      });
    } catch (error: any) {
      throw this.buildMessageError(error.details, 'stripe');
    }
  }

  private mapZenriseErrorToUserFriendlyMessage(errorMessage: string): string {
    return this.matchErrorMessage(errorMessage, ZENRISE_ERROR_MAP)
      ?? 'Hubo un problema procesando el pago. Por favor, intenta con un método de pago diferente o contacta a soporte.';
  }

  private mapStripeErrorToUserFriendlyMessage(errorMessage: string): string {
    return this.matchErrorMessage(errorMessage, STRIPE_ERROR_MAP)
      ?? 'Hubo un problema procesando el pago. Por favor, intenta con otra tarjeta o contacta a soporte.';
  }

  private extractHttpErrorMessage(error: any): string | undefined {
    return error?.error?.error?.message || error?.error?.error || error?.error?.message || error?.message;
  }

  private matchErrorMessage(errorMessage: string, errorMap: ErrorMapping[]): string | null {
    const lower = errorMessage.toLowerCase();
    const match = errorMap.find(entry => entry.keywords.every(kw => lower.includes(kw)));
    return match?.message ?? null;
  }

  private buildMessageError(contentError?: string, provider: 'zenrise' | 'stripe' = 'zenrise') {
    let errorMessage = contentError || this.t.translate('general.subscriptionError');

    if (contentError) {
      if (provider === 'zenrise') {
        errorMessage = this.mapZenriseErrorToUserFriendlyMessage(contentError);
      } else if (provider === 'stripe') {
        errorMessage = this.mapStripeErrorToUserFriendlyMessage(contentError);
      }
    }

    return {
      errorTitle: this.t.translate('general.errorTitle'),
      errorMessage,
    };
  }
}
